import { defineStore } from 'pinia';
import Auth from '@/services/auth';
import { normalizeError } from '@/utils/error-handler';

import { Apii } from '@/services/api.js';
import { generateUUID } from '@/utils/uuid.js';
import { getItem, setItem } from '@/utils/localStorage';
import { router } from '@inertiajs/vue3';

const ApiService = new Apii();

export const useLoginStore = defineStore('login', {
    state: () => ({
        errors: [],
        formErrors: {},
        saving: false,
        loading: false,
        loginCreds: { email: '', password: '' },
        showPassword: false,
        show2FAModal: false,
        showTwoFactor: false,
        showResendCode: false,
        sendingCode: false,
        resending: false,
        code: '',
        resendCodeMessage: 'Resending code ...',
        intended: '',
        user: {},
        otpCode: '',
        showRecoveryCode: false,
        recoveryCode: null,
        phone: '',
        mode: 'login',
        intended: '',
        utmParams: {},
        hasPasskeys: false,
        shouldPromptSetup: false,
    }),
    actions: {
        async login() {
            this.formErrors = {};
            this.errors = [];
            this.loading = true;
            window.axios.get('/sanctum/csrf-cookie').then(() => {
                axios
                    .post('/login', this.loginCreds)
                    .then((response) => {
                        if (response.status === 200) {
                            this.intended = response.data?.intended;
                            this.user = response.data;

                            // Auth is carried by the Sanctum session cookie; we
                            // no longer persist the bearer token (SUPPORT-663 /
                            // F-024). The uuid is a non-secret convenience value.
                            setItem('uuid', response.data.uuid);

                            if (response.data?.uses_2fa) {
                                this.show2FAModal = true;
                                this.loading = false;
                                this.phone = response.data.phone;
                                return false;
                            }

                            if (response.data?.two_factor) {
                                this.loading = false;
                                this.showTwoFactor = true;
                                return false;
                            }

                            if (import.meta.env.VITE_APP_ENV === 'production') {
                                var savedHubspotEmail =
                                    getItem('hubspot_email') || '';
                                const event_id = generateUUID();

                                // Facebook Pixel Login Event
                                if (
                                    typeof fbq !== 'undefined' &&
                                    response.data.hashed_email_sha256 &&
                                    response.data.uuid
                                ) {
                                    fbq(
                                        'track',
                                        'Login',
                                        {
                                            em: response.data
                                                .hashed_email_sha256,
                                            external_id: response.data.uuid,
                                        },
                                        { eventID: event_id }
                                    );
                                }

                                woopra.identify({
                                    email: response.data.email,
                                    hubspot_email: savedHubspotEmail,
                                    name: response.data.name,
                                    uuid: response.data.uuid,
                                    fb_event_id: event_id,
                                });
                                woopra.track('login-my10x', {
                                    name: response.data.name,
                                    email: response.data.email,
                                    uuid: response.data.uuid,
                                    fb_event_id: event_id,
                                });

                                if (dataLayer) {
                                    window.gtag('config', 'G-0RRE7P1WE9');
                                    window.gtag('config', 'G-24V6GCLG87');

                                    window.gtag('event', 'My10x_Login', {
                                        user_properties: {
                                            joined_my10x: 'Yes',
                                        },
                                    });
                                }
                            }

                            // Push to Google Analytics DataLayer
                            if (window.dataLayer) {
                                window.dataLayer.push({
                                    event: 'my10x_login_complete',
                                    login_method: 'traditional',
                                    user_id: response.data.uuid,
                                });
                            }

                            if (response.data) {
                                // Session-cookie auth; persist only the
                                // non-secret uuid (SUPPORT-663 / F-024).
                                setItem('uuid', response.data.uuid);
                                this.loginCreds.email = '';
                                this.loginCreds.password = '';
                                this.show2FAModal = false;
                                this.showTwoFactor = false;
                                this.loading = false; // Check if should prompt for passkey setup

                                // TODO: this is broken now the location.href is used
                                // MARK: this should be fixed
                                this.shouldPromptSetup =
                                    response.data.should_prompt_passkey_setup ||
                                    false;

                                location.href = this.intended;
                            }
                        } else {
                            this.loading = false;
                            this.handleError(response.response);
                        }
                    })
                    .catch((e) => {
                        this.loading = false;
                        this.handleError(e.response);
                    });
            });
        },
        async hash(string) {
            const utf8 = new TextEncoder().encode(string);
            return crypto.subtle.digest('SHA-256', utf8).then((hashBuffer) => {
                const hashArray = Array.from(new Uint8Array(hashBuffer));
                const hashHex = hashArray
                    .map((bytes) => bytes.toString(16).padStart(2, '0'))
                    .join('');
                return hashHex;
            });
        },
        verifyCode() {
            this.loading = true;
            const data = this.showRecoveryCode
                ? { recovery_code: this.recoveryCode }
                : { code: this.otpCode };

            const auth = Auth.twoFactorChallenge(data);
            auth.then((response) => {
                this.loading = false;

                if (import.meta.env.VITE_APP_ENV === 'production') {
                    const event_id = generateUUID();

                    // Facebook Pixel Login Event (after 2FA)
                    if (
                        typeof fbq !== 'undefined' &&
                        this.user.hashed_email_sha256 &&
                        this.user.uuid
                    ) {
                        fbq(
                            'track',
                            'Login',
                            {
                                em: this.user.hashed_email_sha256,
                                external_id: this.user.uuid,
                            },
                            { eventID: event_id }
                        );
                    }

                    var savedHubspotEmail =
                        getItem('hubspot_email') || '';
                    woopra.identify({
                        email: this.user.email,
                        hubspot_email: savedHubspotEmail,
                        name: this.user.name,
                        uuid: this.user.uuid,
                        fb_event_id: event_id,
                    });
                    woopra.track('login-my10x', {
                        name: this.user.name,
                        email: this.user.email,
                        uuid: this.user.uuid,
                    });
                }
                //localStorage.setItem('_token', response.data.token);
                //localStorage.setItem('uuid', response.data.uuid);
                location.href = this.intended;
            }).catch((e) => {
                this.loading = false;
                this.handleError(e.response);
            });
        },
        submitCode() {
            this.showResendCode = false;
            this.resending = false;
            this.resendCodeMessage = 'Resending code ...';
            this.errors = [];
            this.sendingCode = true;
            this.validateCode();
            if (this.code.toString().length === 6) {
                const auth = Auth.submitTwoFA({
                    code: this.code,
                });
                auth.then((response) => {
                    if (!response.data.valid_code) {
                        let message =
                            'The code is not valid. You may resend it.';
                        if (response.data.message === 'please_wait') {
                            message =
                                'Too many attempts. Please wait 5 min before trying again.';
                        }
                        if (response.data.message === 'too_many_tries') {
                            message =
                                'Account is locked. Too many failed attempts.';
                        }
                        this.errors.push({
                            message: message,
                        });
                        this.showResendCode = true;
                        this.sendingCode = false;
                    } else {
                        if (import.meta.env.VITE_APP_ENV === 'production') {
                            const event_id = generateUUID();

                            // Facebook Pixel Login Event (after 2FA)
                            if (
                                typeof fbq !== 'undefined' &&
                                this.user.hashed_email_sha256 &&
                                this.user.uuid
                            ) {
                                fbq(
                                    'track',
                                    'Login',
                                    {
                                        em: this.user.hashed_email_sha256,
                                        external_id: this.user.uuid,
                                    },
                                    { eventID: event_id }
                                );
                            }
                            var savedHubspotEmail =
                                getItem('hubspot_email') || '';
                            woopra.identify({
                                email: this.user.email,
                                hubspot_email: savedHubspotEmail,
                                name: this.user.name,
                                uuid: this.user.uuid,
                                fb_event_id: event_id,
                            });
                            woopra.track('login-my10x', {
                                name: this.user.name,
                                email: this.user.email,
                                uuid: this.user.uuid,
                                fb_event_id: event_id,
                            });
                        }
                        if (this.mode === 'register') {
                            if (import.meta.env.VITE_APP_ENV === 'production') {
                                woopra.track('register-my10x', {
                                    name: this.user.name,
                                    email: this.user.email,
                                    uuid: this.user.uuid,
                                });
                            }
                            location.href = '/quiz';
                        } else if (this.mode === 'login') {
                            if (import.meta.env.VITE_APP_ENV === 'production') {
                                woopra.track('login-my10x', {
                                    name: this.user.name,
                                    email: this.user.email,
                                    uuid: this.user.uuid,
                                });
                            }
                            if (response.data) {
                                // Session-cookie auth completes the 2FA step;
                                // no bearer token to store (SUPPORT-663 / F-024).
                                location.href = this.intended;
                            }
                        }
                    }
                }).catch((e) => {
                    this.handleError(e.response);
                    this.sendingCode = false;
                });
            }
        },
        resendCode() {
            this.resending = true;
            const auth = Auth.resendTwoFA();
            auth.then((response) => {
                if (!response.data.success) {
                    let message = 'The code is not valid. You may resend it.';
                    if (response.data.message === 'please_wait') {
                        message =
                            'Too many attempts. Please wait 5 min before trying again.';
                    }
                    if (response.data.message === 'too_many_tries') {
                        message =
                            'Account is locked. Too many failed attempts.';
                    }
                    this.code = '';
                    this.errors.push({
                        message: message,
                    });
                } else {
                    this.code = '';
                    this.resendCodeMessage = 'Code resent.';
                    this.errors = [];
                }
            }).catch((e) => {
                this.handleError(e.response);
            });
        },
        validatePhoneNumber() {
            this.errors = [];
            this.validateCode();
            if (this.code.toString().length !== 6) {
                if (this.code.toString().length === 6) {
                    const auth = Auth.validatePhoneNumber({ code: this.code });
                    auth.then((response) => {
                        if (!response.data.valid_code) {
                            this.errors.push({
                                message:
                                    'The code is invalid or expired. You may resend it.',
                            });
                            this.showResendCode = true;
                            this.sendingCode = false;
                        } else {
                            //localStorage.setItem('_token', response.data.token);
                            //location.href = '/dashboard';
                        }
                    }).catch((e) => {
                        this.handleError(e.response);
                        this.sendingCode = false;
                    });
                }
            }
        },
        validateCode() {
            if (this.code.toString().length !== 6) {
                this.errors.push({
                    message: 'The SMS code is invalid.',
                });
            }
        },
        checkLength() {
            if (this.code.toString().length > 6) {
                this.code = this.code.toString().slice(0, 6);
            }
        },
        handleError(error) {
            if (!error) return;

            const response = normalizeError(error);
            this.formErrors = {};

            // Handle validation errors
            if (
                response.status === 422 &&
                response.data &&
                response.data.errors
            ) {
                for (const key in response.data.errors) {
                    this.errors.push({ message: response.data.errors[key][0] });
                    this.formErrors[key] = response.data.errors[key][0];
                }
            }

            // Handle authentication errors
            if (response.status === 401 || response.status === 403) {
                location.href = '/login?loginstatus=autologout';
            }
        },
        cancelSms() {
            this.show2FAModal = false;
        },
        cancelTotp() {
            this.showTwoFactor = false;
        },
        gotoTwoFactor() {
            this.showTwoFactor = true;
            this.showRecoveryCode = false;
        },
        close2FAModal() {
            this.show2FAModal = false;
        },
        setUtmParams(params) {
            this.utmParams = params;
            ApiService.apiCall('/utm-params', 'post', params);
        },
        async checkPasskeyAvailability(email) {
            try {
                const response = await ApiService.apiCall(
                    '/user/passkey-status',
                    'post',
                    { email }
                );
                this.hasPasskeys = response.data.has_passkeys;
            } catch (err) {
                console.error(
                    '[LoginStore] Failed to check passkey status:',
                    err
                );
                this.hasPasskeys = false;
            }
        },
    },
});
